import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import authService from '../services/authService';
import { setupAuthInterceptors, clearLegacyAccessToken, getApiErrorMessage } from '../services/http';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Auth design:
 *   JWT → httpOnly cookie → GET /auth/me → React session (memory only)
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = async () => {
    try {
      await authService.logout();
    } catch {
      /* still clear local session */
    }
    setUser(null);
    toast.success('Logged out successfully');
  };

  useEffect(() => {
    clearLegacyAccessToken();
    const eject = setupAuthInterceptors({
      onRefreshFail: () => setUser(null),
    });
    return eject;
  }, []);

  useEffect(() => {
    const hydrateSession = async () => {
      try {
        const me = await authService.getMe();
        setUser(me);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    hydrateSession();
  }, []);

  const login = async (email, password) => {
    try {
      await authService.login(email, password);
      const me = await authService.getMe();
      setUser(me);
      toast.success('Login successful!');
      return true;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Login failed'));
      return false;
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const data = await authService.updateProfile(profileData);
      setUser(data);
      toast.success('Profile updated successfully');
      return true;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Profile update failed'));
      return false;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      return true;
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Password change failed'));
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
